
import {
  createApp,
  computed,
  onBeforeMount,
  onMounted,
  onUnmounted,
  ref,
  watch,
  nextTick,
} from 'vue';
import ShokzMember from '@theme/shokz-member';

/**
 * @import {SimpleToast} from '@theme/component-simple-toast'
 */

/** @type {SimpleToast} */
const simpleToast = document.getElementById('simple-toast');

/**
 * 从接口 tiers[0].id（如 "Level 3"）中解析出等级数字，作为当前会员等级级别（1-based）。
 * @param {string | undefined} id
 * @returns {number}
 */
function parseTierLevelFromTierId(id) {
  if (id == null || typeof id !== 'string') return 1;
  const match = id.match(/\d+/);
  return match ? parseInt(match[0], 10) : 1;
}

// 将 ISO 日期转成 "Month Day, Year" 的展示格式，用于等级提示文案。
function formatDate(dateString) {
  const date = new Date(dateString);
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  return `${month} ${day}, ${year}`;
}

/**
 * 
 * @param {HubJoinedInfo} section 
 * @returns 
 */
function createHubJoinedInfoVueEntry(section) {
  const app = createApp({
    delimiters: ['${', '}'],
    setup() {
      // 视图状态
      const loading = ref(true);
      const currentPoints = ref(0);
      const nextPoints = ref(0);
      const currentTier = ref(1);
      const currentTierName = ref('');
      const nextTierName = ref('');
      const subtitle = ref('');
      const tierHint = ref('');
      const progress = ref(0);
      const isProgressFull = ref(false);
      const tierLevelEl = ref(null);
      /** 与 CSS 中 max(..., 8px) / max(..., 50px) 一致，用于三角对准真实进度 */
      const popArrowShiftPx = ref(0);

      // 进度条依赖 CSS 变量，交由 Vue 统一输出，避免手动 setProperty。
      const progressStyle = computed(() => ({
        '--progress-blocks-count': window.ShokzMemberSettings?.tiers_count || 1,
        '--progress': progress.value,
        '--pop-arrow-shift': `${popArrowShiftPx.value}px`,
      }));

      /**
       * 与 section-hub-joined-info.css 中 .tier-card__level__pop 的 left: max(...) 断点一致。
       * @returns {number}
       */
      function getMinPadForTierPopClampedLeft() {
        if (typeof window === 'undefined') return 0;
        const iw = window.innerWidth;
        if (iw <= 768) return 50;
        if (iw <= 1280) return 8;
        return 0;
      }

      function updatePopArrowShift() {
        if (typeof window === 'undefined') return;
        const root = tierLevelEl.value;
        if (!root) return;

        if (window.innerWidth > 1280 || isProgressFull.value) {
          popArrowShiftPx.value = 0;
          return;
        }

        const wrap = root.querySelector('.hub-joined-info__tier-card__level__bar-wrapper');
        if (!wrap) return;
        const W = wrap.getBoundingClientRect().width;
        if (!(W > 0)) {
          popArrowShiftPx.value = 0;
          return;
        }

        const P = Math.min(1, Math.max(0, Number(progress.value) || 0));
        const minPad = getMinPadForTierPopClampedLeft();
        const L = Math.max(P * W, minPad);
        const trueX = P * W;
        popArrowShiftPx.value = trueX - L;
      }

      let popArrowResizeObserver;
      /** @type {(() => void) | undefined} */
      let onPopArrowWindowResize;

      onMounted(() => {
        nextTick(() => {
          updatePopArrowShift();
          const root = tierLevelEl.value;
          const wrap = root?.querySelector('.hub-joined-info__tier-card__level__bar-wrapper');
          if (wrap && typeof ResizeObserver !== 'undefined') {
            popArrowResizeObserver = new ResizeObserver(() => {
              updatePopArrowShift();
            });
            popArrowResizeObserver.observe(wrap);
          }
          onPopArrowWindowResize = () => {
            nextTick(() => updatePopArrowShift());
          };
          window.addEventListener('resize', onPopArrowWindowResize);

          if (typeof window !== 'undefined') {
            window[DEBUG_SET_PROGRESS_GLOBAL] = debugHubJoinedInfoSetProgress;
          }
        });
      });

      watch(
        () => [progress.value, isProgressFull.value, loading.value],
        () => {
          nextTick(() => updatePopArrowShift());
        },
      );

      const DEBUG_SET_PROGRESS_GLOBAL = 'debugHubJoinedInfoSetProgress';

      /**
       * 控制台调试：写入进度 0~1（同步 Vue 的 progress 与内联 --progress），并刷新三角偏移。
       * 满级时 isProgressFull 为 true 会强制三角偏移为 0，可传 `{ isProgressFull: false }` 预览非满级条。
       *
       * @example
       * debugHubJoinedInfoSetProgress(0)
       * debugHubJoinedInfoSetProgress(0.12, { isProgressFull: false })
       *
       * @param {number} value
       * @param {{ isProgressFull?: boolean }} [options]
       * @returns {number} 实际写入的进度
       */
      function debugHubJoinedInfoSetProgress(value, options = {}) {
        const n = Math.min(1, Math.max(0, Number(value)));
        if (Number.isNaN(n)) {
          console.warn(`[${DEBUG_SET_PROGRESS_GLOBAL}] invalid number`, value);
          return progress.value;
        }
        progress.value = n;
        if (options && typeof options.isProgressFull === 'boolean') {
          isProgressFull.value = options.isProgressFull;
        }
        nextTick(() => updatePopArrowShift());
        return n;
      }

      // 模板 class 绑定辅助函数
      const isTierProgressActive = (tierLevel) => tierLevel <= currentTier.value;
      const isTierDecorationVisible = (tierLevel) => tierLevel === currentTier.value;

      /**
       * 将会员中心接口数据映射为当前模块所需的显示状态。
       * 等级与进度条以接口 memberData.tiers[0] 为准，不再用 theme settings 的积分分段推算等级。
       * @param {import("@theme/shokz-member-type").MemberData} memberData
       * @returns
       */
      const render = (memberData) => {
        if (memberData == undefined) {
          console.error('<hub-joined-info>: memberData is undefined');
          return;
        }

        const tiersCount = window.ShokzMemberSettings?.tiers_count || 1;
        const tier0 = memberData.tiers?.[0];
        if (tier0 == undefined) {
          console.error('<hub-joined-info>: memberData.tiers[0] is missing');
          return;
        }

        /** 当前等级积分余额（接口字段） */
        const points = Number(tier0.points) || 0;
        /** 当前等级门槛 */
        const threshold = Number(tier0.threshold) || 0;
        /** 从 tiers[0].id 解析的当前会员等级级别（1..N） */
        const tierLevel = parseTierLevelFromTierId(tier0.id);
        const next = tier0.next;
        const hasNext =
          next != null &&
          typeof next === 'object' &&
          typeof next.points === 'number';

        currentPoints.value = points;
        currentTier.value = tierLevel;
        // currentTierName.value = tier0.name || '';
        // 从 ShokzMemberSettings 中获取当前等级名称
        currentTierName.value = window.ShokzMemberSettings.tiers.find((tier) => tier.tier == tierLevel)?.tier_name || '';

        const isMaxTier = tierLevel >= tiersCount || !hasNext;

        if (isMaxTier) {
          // 满级：固定文案 + 进度满格
          subtitle.value = section.dataJson?.subtitle_max_level || '';
          nextPoints.value = 0;
          progress.value = 1;
          isProgressFull.value = true;
        } else {
          // 非满级：距离下一级积分与下一级名称来自接口 next
          nextPoints.value = Math.max(0, Number(next.points) || 0);
          nextTierName.value = next.name || '';
          subtitle.value = decodeURIComponent(
            (section.dataJson?.subtitle || '')
              .replace('${points}', String(nextPoints.value))
              .replace('${tierName}', nextTierName.value)
          );

          // 分段数不变；已完成整段 + 当前段内按「门槛 ~ 下一级门槛（points + next.points）」比例
          const segmentWidth = 1 / tiersCount;
          const completedSegments = (tierLevel - 1) * segmentWidth;
          const nextTierThreshold = points + nextPoints.value;
          const span = nextTierThreshold - threshold;
          let stageProgress = span > 0 ? (points - threshold) / span : 0;
          stageProgress = Math.min(1, Math.max(0, stageProgress));
          progress.value = completedSegments + segmentWidth * stageProgress;
          isProgressFull.value = false;
        }

        // 待生效积分提示文案（主题配置中占位符可能是 ${points} 或 HTML 实体 $&lcub;points&rcub;）
        const takeEffectPoints = memberData.accounts[0].pending || 0;
        tierHint.value = takeEffectPoints > 0
          ? (section.dataJson?.tier_hint || '').replace(/\$\{points\}|\$&lcub;points&rcub;/g, String(takeEffectPoints))
          : '';
      };

      /**
       * 会员信息初始化后的订阅者
       * @type {Function}
       */
      let memberInfoInitializedUnsubscriber;
      onBeforeMount(() => {
        memberInfoInitializedUnsubscriber = subscribe(PUB_SUB_EVENTS.memberInfoInitialized, onMemberInfoInitialized);

        if (ShokzMember.memberData != undefined) {
          render(ShokzMember.memberData);
          loading.value = false;
        }
      });

      onUnmounted(() => {
        if (memberInfoInitializedUnsubscriber) {
          memberInfoInitializedUnsubscriber();
        }
        popArrowResizeObserver?.disconnect();
        if (onPopArrowWindowResize) {
          window.removeEventListener('resize', onPopArrowWindowResize);
        }
        if (typeof window !== 'undefined' && window[DEBUG_SET_PROGRESS_GLOBAL] === debugHubJoinedInfoSetProgress) {
          delete window[DEBUG_SET_PROGRESS_GLOBAL];
        }
      });

      // ============ 方法 ============
      /**
       * 会员信息初始化后加载模块
       * @param {{data: import("./shokz-member-type").MemberData}} data 
       */
      const onMemberInfoInitialized = (data) => {
        reload();
      }

      const reload = () => {
        loading.value = true;
        fetchData().then(data => {
          render(data);
        }).catch(error => {
          simpleToast.show(error?.message || 'An unknown error occurred, please try again later.');
          console.error('[hub-joined-info]: fetchData error', error);
        }).finally(() => {
          loading.value = false;
        });
      }

      const fetchData = () => {
        return ShokzMember.getMemberDataByOnceCall();
      }

      return {
        loading,
        currentPoints,
        nextPoints,
        currentTierName,
        nextTierName,
        subtitle,
        tierHint,
        progressStyle,
        isProgressFull,
        isTierProgressActive,
        isTierDecorationVisible,
        tierLevelEl,
      };
    }
  });

  app.mount(section.querySelector('[data-vue-render]'));
  return app;
}

class HubJoinedInfo extends HTMLElement {
  // 来自 Liquid 的文案配置（script[data-json]）
  /**
   * @type {{subtitle: string, subtitle_max_level: string, tier_hint: string}}
   */
  dataJson = {};
  app = undefined;

  constructor() {
    super();
    this.dataJson = JSON.parse(this.querySelector('[data-json]').innerHTML);
  }

  connectedCallback() {
    this.app = createHubJoinedInfoVueEntry(this);
  }

  disconnectedCallback() {
    if (this.app) this.app.unmount();
  }
}

if (!customElements.get('hub-joined-info')) {
  customElements.define('hub-joined-info', HubJoinedInfo);
}